/**
 * Source account picker — the "% of Account" / zero-base "% of other
 * accounts" selection, from the Claude Design "Source Account Picker":
 *
 *  - stacked search: typing filters live; Enter pins the term as a chip, and
 *    each chip after the first carries an AND / OR toggle (click to flip);
 *    Backspace on an empty input pops the last chip
 *  - results grouped by department with a tri-state group checkbox, a
 *    per-group "Select all N" / "Deselect all", and a "Select all N matching"
 *    across the current filter
 *  - groups start collapsed unless they hold a selection; any filter expands
 *    them all; the caret overrides either way
 *  - footer counts + a "Review selection" tray of removable chips
 *
 * Selection is controlled: `onChange(nextIds)` fires once per user action
 * with the full next set, so callers can persist per change (the driver pane)
 * or hold it in local state (the zero-base modal).
 */

import React, { useMemo, useRef, useState } from 'react';
import { Search, X, ChevronRight, Check } from 'lucide-react';

export interface SourceAccountOption {
  chartOfAccountId: number;
  accountName: string;
  accountNumber: string;
  department: string;
}

export interface SourceAccountPickerProps {
  accounts: SourceAccountOption[];
  selectedIds: number[];
  onChange: (nextIds: number[]) => void;
  disabled?: boolean;
  /** Default join for a newly pinned term. */
  defaultJoin?: 'AND' | 'OR';
  /** List height cap (px). */
  maxListHeight?: number;
  /** Compact density for popovers. */
  dense?: boolean;
  /** Footer noun, e.g. "feed this driver". */
  footerVerb?: string;
  /** Autofocus the search input on mount (popovers). */
  autoFocus?: boolean;
}

interface Chip { text: string; op: 'AND' | 'OR' }

const ORANGE = 'var(--lodge-orange-500, var(--fv3-accent, #FF7A00))';

function Checkbox({ state, size = 16 }: { state: 'on' | 'some' | 'off'; size?: number }) {
  if (state === 'off') {
    return <span style={{ width: size, height: size, borderRadius: 4, border: '1.5px solid #C8C8D4', background: '#fff', flex: 'none', display: 'inline-block' }} />;
  }
  return (
    <span style={{ width: size, height: size, borderRadius: 4, background: ORANGE, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
      {state === 'on'
        ? <Check size={size - 5} strokeWidth={3.5} color="#fff" />
        : <span style={{ width: size / 2, height: 2, background: '#fff', borderRadius: 2 }} />}
    </span>
  );
}

export function SourceAccountPicker({
  accounts,
  selectedIds,
  onChange,
  disabled = false,
  defaultJoin = 'AND',
  maxListHeight = 430,
  dense = false,
  footerVerb = 'feed this driver',
  autoFocus = false,
}: SourceAccountPickerProps) {
  const [query, setQuery] = useState('');
  const [chips, setChips] = useState<Chip[]>([]);
  const [expandOverride, setExpandOverride] = useState<Record<string, boolean>>({});
  const [reviewOpen, setReviewOpen] = useState(false);
  const everFiltered = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const hasFilter = chips.length > 0 || query.trim().length > 0;
  if (hasFilter) everFiltered.current = true;

  const matches = (a: SourceAccountOption) => {
    const hay = `${a.accountName} ${a.accountNumber} ${a.department}`.toLowerCase();
    let ok = true;
    chips.forEach((c, i) => {
      const m = hay.includes(c.text.toLowerCase());
      ok = i === 0 ? m : c.op === 'OR' ? ok || m : ok && m;
    });
    const q = query.trim().toLowerCase();
    if (q) ok = ok && hay.includes(q);
    return ok;
  };

  const rows = useMemo(() => accounts.filter(matches), [accounts, chips, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const groups = useMemo(() => {
    const order: string[] = [];
    const byDept = new Map<string, SourceAccountOption[]>();
    rows.forEach((a) => {
      const key = a.department || '(No department)';
      if (!byDept.has(key)) { byDept.set(key, []); order.push(key); }
      byDept.get(key)!.push(a);
    });
    return order.map((name) => {
      const list = byDept.get(name)!;
      const ids = list.map((a) => a.chartOfAccountId);
      const nSel = ids.filter((id) => selected.has(id)).length;
      const all = nSel === ids.length;
      const override = expandOverride[name];
      const expanded = override === undefined ? everFiltered.current || nSel > 0 : override;
      return { name, list, ids, nSel, all, expanded };
    });
  }, [rows, selected, expandOverride]);

  const setSel = (ids: number[], on: boolean) => {
    if (disabled) return;
    const next = new Set(selected);
    ids.forEach((id) => { if (on) next.add(id); else next.delete(id); });
    onChange(Array.from(next));
  };

  const allMatchSelected = rows.length > 0 && rows.every((a) => selected.has(a.chartOfAccountId));
  const selectedList = accounts.filter((a) => selected.has(a.chartOfAccountId));

  const pad = dense ? '7px 10px 7px 30px' : '9px 14px 9px 38px';
  const headPad = dense ? '8px 10px 8px 8px' : '10px 14px 10px 10px';
  const nameSize = dense ? 12.5 : 13.5;

  return (
    <div className="flex flex-col gap-3" style={{ opacity: disabled ? 0.6 : 1 }}>
      {/* Header */}
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-[13px] font-semibold text-slate-800">Source Accounts</div>
        <div className="flex items-center gap-3">
          <span className="font-mono text-[12px] text-slate-500">{selectedIds.length} selected</span>
          {selectedIds.length > 0 && (
            <button type="button" onClick={() => { onChange([]); setReviewOpen(false); }} disabled={disabled} className="text-[12px] font-medium hover:opacity-80" style={{ color: ORANGE }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Search + chips */}
      <div
        className="flex flex-wrap items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 min-h-[40px] focus-within:border-orange-400"
        onClick={() => inputRef.current?.focus()}
      >
        <Search size={15} className="text-slate-400 flex-none" />
        {chips.map((c, i) => (
          <span key={`${c.text}-${i}`} className="flex items-center gap-1.5">
            {i > 0 && (
              <button
                type="button"
                title="Switch AND / OR"
                onClick={(e) => { e.stopPropagation(); setChips((prev) => prev.map((p, j) => (j === i ? { ...p, op: p.op === 'AND' ? 'OR' : 'AND' } : p))); }}
                className="font-mono text-[10px] font-semibold tracking-wider text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 hover:bg-slate-200 hover:text-slate-900"
              >
                {c.op}
              </button>
            )}
            <span className="inline-flex items-center gap-1 rounded-full text-[12px] font-medium" style={{ background: 'var(--fv3-accent-tint, rgba(255,122,0,.10))', border: '1px solid var(--fv3-accent-line, rgba(255,122,0,.28))', color: 'var(--fv3-accent-ink, #B35600)', padding: '3px 6px 3px 10px' }}>
              {c.text}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setChips((prev) => prev.filter((_, j) => j !== i)); }}
                className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full hover:bg-orange-200/60"
                style={{ color: 'var(--fv3-accent-ink, #B35600)' }}
              >
                <X size={10} strokeWidth={3} />
              </button>
            </span>
          </span>
        ))}
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && query.trim()) {
              e.preventDefault();
              setChips((prev) => [...prev, { text: query.trim(), op: defaultJoin }]);
              setQuery('');
            } else if (e.key === 'Backspace' && !query && chips.length) {
              setChips((prev) => prev.slice(0, -1));
            }
          }}
          placeholder={chips.length ? 'Add another term…' : 'Search accounts…'}
          className="flex-1 min-w-[120px] border-none outline-none bg-transparent text-[13px] text-slate-900 h-[26px]"
        />
      </div>

      <div className="flex items-center justify-between gap-3 min-h-[20px]">
        <div className="text-[12px] text-slate-400">
          {chips.length ? 'Enter adds a term · click AND / OR to change how terms combine' : 'Type a term and press Enter to stack filters'}
        </div>
        {hasFilter && rows.length > 0 && (
          <button type="button" onClick={() => setSel(rows.map((a) => a.chartOfAccountId), !allMatchSelected)} disabled={disabled} className="text-[12px] font-semibold whitespace-nowrap hover:opacity-80" style={{ color: ORANGE }}>
            {allMatchSelected ? 'Deselect' : 'Select'} all {rows.length} matching
          </button>
        )}
      </div>

      {/* Groups */}
      <div className="rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: maxListHeight }}>
          {groups.map((g) => {
            const state: 'on' | 'some' | 'off' = g.all ? 'on' : g.nSel > 0 ? 'some' : 'off';
            const toggleAll = (e: React.MouseEvent) => {
              e.stopPropagation();
              setExpandOverride((prev) => ({ ...prev, [g.name]: g.expanded }));
              setSel(g.ids, !g.all);
            };
            return (
              <div key={g.name}>
                <div
                  onClick={() => setExpandOverride((prev) => ({ ...prev, [g.name]: !g.expanded }))}
                  className="flex items-center gap-2.5 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100 sticky top-0"
                  style={{ padding: headPad }}
                >
                  <span className="inline-flex items-center justify-center w-[18px] h-[18px] text-slate-500 transition-transform" style={{ transform: g.expanded ? 'rotate(90deg)' : 'rotate(0deg)' }}>
                    <ChevronRight size={14} strokeWidth={2.5} />
                  </span>
                  <span onClick={toggleAll} className="inline-flex"><Checkbox state={state} /></span>
                  <span className="text-[12px] font-bold tracking-wide text-slate-900 uppercase">{g.name}</span>
                  <span className="font-mono text-[11px] text-slate-400">{g.nSel ? `${g.nSel} of ${g.ids.length} selected` : `${g.ids.length} accounts`}</span>
                  <button type="button" onClick={toggleAll} disabled={disabled} className="ml-auto text-[12px] font-semibold hover:opacity-80" style={{ color: ORANGE }}>
                    {g.all ? 'Deselect all' : `Select all ${g.ids.length}`}
                  </button>
                </div>
                {g.expanded && g.list.map((a) => {
                  const on = selected.has(a.chartOfAccountId);
                  return (
                    <div
                      key={a.chartOfAccountId}
                      onClick={() => { setExpandOverride((prev) => ({ ...prev, [g.name]: true })); setSel([a.chartOfAccountId], !on); }}
                      className="flex items-center gap-2.5 border-b border-slate-100 cursor-pointer bg-white hover:bg-slate-50"
                      style={{ padding: pad }}
                    >
                      <Checkbox state={on ? 'on' : 'off'} />
                      <div className="flex flex-col min-w-0" style={{ gap: 1 }}>
                        <span className="text-slate-900 truncate" style={{ fontSize: nameSize }}>{a.accountName}</span>
                        <span className="font-mono text-[11px] text-slate-400">{a.accountNumber}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
          {groups.length === 0 && (
            <div className="px-5 py-8 text-center text-[13px] text-slate-400">No accounts match these filters.</div>
          )}
        </div>
      </div>

      {/* Footer + review tray */}
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[12px] text-slate-500">
          {selectedIds.length} accounts {footerVerb} · showing {rows.length} of {accounts.length}
        </span>
        {selectedIds.length > 0 && (
          <button type="button" onClick={() => setReviewOpen((v) => !v)} className="text-[12px] font-semibold hover:opacity-80" style={{ color: ORANGE }}>
            {reviewOpen ? 'Hide selection' : 'Review selection'}
          </button>
        )}
      </div>
      {reviewOpen && selectedList.length > 0 && (
        <div className="rounded-xl border border-slate-200 p-3 flex flex-wrap gap-1.5 overflow-y-auto" style={{ maxHeight: 150, background: '#FAFAFD' }}>
          {selectedList.map((a) => (
            <span key={a.chartOfAccountId} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 rounded-full text-[12px] text-slate-800" style={{ padding: '4px 6px 4px 10px' }}>
              {a.accountName}
              <span className="font-mono text-[10px] text-slate-400">{a.accountNumber}</span>
              <button type="button" onClick={() => setSel([a.chartOfAccountId], false)} disabled={disabled} className="inline-flex items-center justify-center w-[15px] h-[15px] rounded-full text-slate-500 hover:bg-slate-200 hover:text-slate-900">
                <X size={10} strokeWidth={3} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
